/**
 * /admin/data-export — download converted ball-by-ball data.
 *
 * Sits under System, below Database, because it is the same kind of
 * thing: a direct window onto what is actually stored rather than a
 * workflow screen.
 *
 * One competition (tournament-season) per download. The backend streams
 * the CSV, so a big season starts downloading immediately instead of
 * making the server build the whole file first — that is deliberate and
 * the reason this is CSV rather than xlsx. A season workbook would have
 * to be assembled in memory on the server, which is what took prod down
 * on 2026-08-31.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Download, Loader2, Search, Database as DatabaseIcon,
} from "lucide-react";
import {
  downloadCompetitionBallsCsv, listDataExportCompetitions,
} from "../../api/owner.api";

export default function AdminDataExport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  // id of the competition currently downloading, so only its own button
  // shows a spinner rather than disabling the whole table.
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await listDataExportCompetitions();
        if (alive) setRows(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) =>
      `${c.name} ${c.season} ${c.tournament}`.toLowerCase().includes(needle));
  }, [rows, q]);

  const totalMatches = useMemo(
    () => rows.reduce((n, c) => n + (c.matches || 0), 0), [rows]);

  async function download(c) {
    setBusyId(c.id);
    setNote(null);
    try {
      const { filename } = await downloadCompetitionBallsCsv(c.id);
      setNote(`Downloaded ${filename}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <header>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <DatabaseIcon className="w-6 h-6" /> Data export
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Converted ball-by-ball data, one CSV per tournament-season. Every
          delivery that has been ingested and converted into the app, with
          its scoring, analytics, wagon-wheel and dismissal columns.
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      {note && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
          {note}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by tournament or season…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-bg-secondary border border-border-default text-sm"
          />
        </div>
        {!loading && (
          <span className="text-xs text-text-secondary">
            {filtered.length} of {rows.length} competitions · {totalMatches.toLocaleString()} matches
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-6 text-text-secondary">
          <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading competitions…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-sm text-text-secondary border border-border-default rounded-lg">
          {rows.length === 0
            ? "No competitions have ingested matches yet."
            : "Nothing matches that filter."}
        </div>
      ) : (
        <div className="border border-border-default rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary text-text-secondary">
              <tr>
                <th className="text-left font-medium px-3 py-2">Competition</th>
                <th className="text-left font-medium px-3 py-2">Season</th>
                <th className="text-left font-medium px-3 py-2">Tournament</th>
                <th className="text-right font-medium px-3 py-2">Matches</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-border-default">
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 text-text-secondary">{c.season || "—"}</td>
                  <td className="px-3 py-2 text-text-secondary">{c.tournament || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.matches}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => download(c)}
                      disabled={busyId === c.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border-default hover:border-emerald-500/60 hover:text-emerald-300 disabled:opacity-50 transition-colors"
                    >
                      {busyId === c.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Download className="w-4 h-4" />}
                      {busyId === c.id ? "Preparing…" : "CSV"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-text-secondary">
        A large season is a large file — the download begins as soon as the
        server starts producing rows, so the browser may show it growing for
        a while before it completes.
      </p>
    </div>
  );
}
